import { useCallback } from 'react';
import { useFormContext } from '@common-fe/common-fe';

import {
  PayoutDefinitionField,
  PayoutDefinitionFormValues,
  PayoutOrderPlan,
  PayoutOrderService,
} from '../../../PayoutDefinitionSetup.types';
import { usePayoutOrderModalsStore } from '../stores/usePayoutOrderModals.store';

import { getServiceColumnErrorKey } from './usePayoutOrderValidation';
import { collectValidationErrors } from './usePayoutOrderValidation.utils';

interface UpdateServicePlanParams {
  categoryType: string;
  rowId: string;
  order: number;
  plan: PayoutOrderPlan | null;
}

const usePayoutOrderPlans = () => {
  const { setValue, getValues, clearErrors, setError } = useFormContext<PayoutDefinitionFormValues>();

  /** Clears validation error for a specific service column (keyed by unique rowId) */
  const clearServiceColumnError = useCallback(
    (rowId: string, order: number) => {
      const fieldKey = getServiceColumnErrorKey(rowId, order);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      clearErrors(fieldKey as any);
    },
    [clearErrors]
  );

  const revalidateService = useCallback(
    (service: PayoutOrderService) => {
      if (!usePayoutOrderModalsStore.getState().isValidationTriggered) return;

      const columnsCount = getValues(PayoutDefinitionField.columnsCount);

      // Clear all columns for this service, then reapply only the current errors.
      const fieldKeys = Array.from({ length: columnsCount }, (_, col) =>
        getServiceColumnErrorKey(service.rowId, col + 1)
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      clearErrors(fieldKeys as any);

      const errors = collectValidationErrors([service], columnsCount);
      for (const error of errors) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setError(error.key as any, { type: error.type, message: error.message });
      }
    },
    [getValues, clearErrors, setError]
  );

  /** Sets or removes a plan for a specific service at a given column (order) */
  const handleUpdateServicePlan = useCallback(
    ({ categoryType, rowId, order, plan }: UpdateServicePlanParams) => {
      clearServiceColumnError(rowId, order);
      const currentCategories = getValues(PayoutDefinitionField.categories) || [];

      let updatedService: PayoutOrderService | null = null;

      const updatedCategories = currentCategories.map((category) => {
        if (category.categoryType !== categoryType) return category;

        return {
          ...category,
          services: category.services.map((service) => {
            if (service.rowId !== rowId) return service;

            const filteredPlans = service.plans.filter((p) => p.order !== order);
            const newPlans = plan ? [...filteredPlans, plan] : filteredPlans;
            updatedService = {
              ...service,
              plans: newPlans.sort((a, b) => a.order - b.order),
            };
            return updatedService;
          }),
        };
      });

      setValue(PayoutDefinitionField.categories, updatedCategories, { shouldDirty: true });

      // Re-validate this service so stale order/duplicate errors stay in sync with the change
      if (updatedService) {
        revalidateService(updatedService);
      }
    },
    [clearServiceColumnError, getValues, setValue, revalidateService]
  );

  /** Sets the same plan for all services within a category at a given column (order) */
  const handleUpdateCategoryPlan = useCallback(
    (categoryType: string, order: number, plan: PayoutOrderPlan | null) => {
      const currentCategories = getValues(PayoutDefinitionField.categories) || [];
      const planWithOrder = plan ? { ...plan, order } : null;

      // Clear errors for the specific column of all services in this category
      const category = currentCategories.find((c) => c.categoryType === categoryType);
      if (category) {
        category.services.forEach((service) => clearServiceColumnError(service.rowId, order));
      }

      const updatedCategories = currentCategories.map((category) => {
        if (category.categoryType !== categoryType) return category;

        return {
          ...category,
          services: category.services.map((service) => {
            const filteredPlans = service.plans.filter((p) => p.order !== order);
            const newPlans = planWithOrder ? [...filteredPlans, planWithOrder] : filteredPlans;

            return {
              ...service,
              plans: newPlans.sort((a, b) => a.order - b.order),
            };
          }),
        };
      });

      setValue(PayoutDefinitionField.categories, updatedCategories, { shouldDirty: true });

      // Re-validate all services in the category so stale order/duplicate errors stay in sync
      const updatedCategory = updatedCategories.find((c) => c.categoryType === categoryType);
      if (updatedCategory) {
        updatedCategory.services.forEach((service) => {
          revalidateService(service);
        });
      }
    },
    [clearServiceColumnError, getValues, setValue, revalidateService]
  );

  /** Removes all plans at a given column order (when a column is removed) */
  const handleRemoveColumnPlans = useCallback(
    (removedOrder: number) => {
      const currentCategories = getValues(PayoutDefinitionField.categories) || [];

      const updatedCategories = currentCategories.map((category) => ({
        ...category,
        services: category.services.map((service) => ({
          ...service,
          plans: service.plans
            .filter((p) => p.order !== removedOrder)
            .map((p) => (p.order > removedOrder ? { ...p, order: p.order - 1 } : p))
            .sort((a, b) => a.order - b.order),
        })),
      }));

      setValue(PayoutDefinitionField.categories, updatedCategories, { shouldDirty: true });
    },
    [getValues, setValue]
  );

  /** Clears all plans from all services */
  const handleClearAllPlans = useCallback(() => {
    const currentCategories = getValues(PayoutDefinitionField.categories) || [];

    const updatedCategories = currentCategories.map((category) => ({
      ...category,
      services: category.services.map((service) => ({
        ...service,
        plans: [],
      })),
    }));

    setValue(PayoutDefinitionField.categories, updatedCategories, { shouldDirty: true });
  }, [getValues, setValue]);

  return {
    handleUpdateServicePlan,
    handleUpdateCategoryPlan,
    handleRemoveColumnPlans,
    handleClearAllPlans,
  };
};

export default usePayoutOrderPlans;
